import * as React from 'react';
import styled, {css} from 'styled-components';
import {darken} from 'polished';
import DropdownItem, {
  StyledDropdownItem,
  DropdownItemProps} from './components/Item';
import handleBlur from 'hooks/use-click-outside';
import {DropdownProvider} from './DropdownContext';

export enum DropdownVariant {
  Default = 'default',
}

export enum DropdownScale {
  Default = 'default',
}

export enum DropdownSide {
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
  Left = 'left',
}

export enum DropdownAlign {
  Left = 'left',
  Right = 'right',
  Center = 'center',
}

export interface DropdownProps {
  trigger(
    open: () => void,
    close: () => void,
  ): React.ReactNode;
  children:
    | React.ComponentElement<DropdownItemProps, any>
    | React.ComponentElement<DropdownItemProps, any>[]
    | JSX.Element
    | JSX.Element[];
  variant?: DropdownVariant;
  scale?: DropdownScale;
  side?: DropdownSide;
  align?: DropdownAlign;
  gap?: number;
}

export const StyledDropdown = styled.div`
  position: relative;
`;

const Children = styled.div<Pick<DropdownProps,
  | 'variant'
  | 'side'
  | 'align'
  | 'scale'
  | 'gap'>>`
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: ${({gap}) => `${gap}px`};
  position: absolute;
  z-index: 5;
  
  ${({variant, theme}) => {
    switch (variant) {
      case DropdownVariant.Default: return css`
        box-shadow: 5px 5px 60px ${darken(.04, theme.variables.backgroundColor)};
        background-color: ${theme.variables.accentBackgroundColor};
      `;
    }
  }};

  ${({scale, gap}) => {
    switch (scale) {
      case DropdownScale.Default: return css`
        border-radius: ${gap ? '5px' : '0 0 5px 5px'};
        
        ${StyledDropdownItem} {
          padding: 10px 14px;
          font-size: 15px;
          line-height: 20px;
        }
      `;
    }
  }};

  ${({side}) => {
    switch (side) {
      case DropdownSide.Top: return css`
        bottom: 100%;
      `;
      case DropdownSide.Right: return css`
        left: 100%;
      `;
      case DropdownSide.Bottom: return css`
        top: 100%;
      `;
      case DropdownSide.Left: return css`
        right: 100%;
      `;
    }
  }};

  ${({align, side}) => {
    switch (align) {
      case DropdownAlign.Left: return (
        side === DropdownSide.Top && css`
          left: 0;
        `
        || side === DropdownSide.Right && css`
          top: 0;
        `
        || side === DropdownSide.Bottom && css`
          left: 0;
        `
        || side === DropdownSide.Left && css`
          top: 0;
        `
      );
      case DropdownAlign.Right: return (
        side === DropdownSide.Top && css`
          right: 0;
        `
        || side === DropdownSide.Right && css`
          bottom: 0;
        `
        || side === DropdownSide.Bottom && css`
          right: 0;
        `
        || side === DropdownSide.Left && css`
          top: 100%;
        `
      );
      case DropdownAlign.Center: return (
        side === DropdownSide.Top && css`
          left: 0;
          right: 0;
        `
        || side === DropdownSide.Right && css`
          top: 0;
          bottom: 0;
        `
        || side === DropdownSide.Bottom && css`
          left: 0;
          right: 0;
        `
        || side === DropdownSide.Left && css`
          left: 0;
          right: 0;
        `
      );
    }
  }};
`;

/**
 * This is a dropdown list.
 * @param {DropdownProps} props
 * @return {React.ReactElement<DropdownProps>}
 * @constructor
 */
function Dropdown(
    props: DropdownProps,
): React.ReactElement<DropdownProps> {
  const ref = React.createRef<HTMLDivElement>();

  const [
    isToggle, setToggle,
  ] = React.useState<boolean>(false);

  /**
   * Open the dropdown.
   */
  function open(): void {
    setToggle(true);
  }

  /**
   * Close the dropdown.
   */
  function close(): void {
    setToggle(false);
  }

  /**
   * This is the blur handler.
   */
  handleBlur(ref, () => {
    close();
  });

  return (
    <DropdownProvider
      isToggle={isToggle}
      open={open}
      close={close}>
      <StyledDropdown ref={ref}>
        {props.trigger(
            open,
            close,
        )}
        {isToggle && (
          <Children
            variant={props.variant}
            side={props.side}
            align={props.align}
            scale={props.scale}
            gap={props.gap}>
            {props.children}
          </Children>
        )}
      </StyledDropdown>
    </DropdownProvider>
  );
}

Dropdown.defaultProps = {
  variant: DropdownVariant.Default,
  scale: DropdownScale.Default,
  side: DropdownSide.Bottom,
};

Dropdown.Item = DropdownItem;

export default Dropdown;
